import { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getCities, deleteCity, getCountries, getZones, exportCities, importCities } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SelectBox from "../components/UI/SelectBox";
import ConfirmModal from "../components/UI/ConfirmModal";
import ImportExportBar from "../components/UI/ImportExportBar";
import { notify } from "../utils/notify";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { I18nContext, useTranslation } from "../context/I18nContext";

export default function Cities() {
  // City (and country/zone) names can come back translated per the
  // signed-in user's own language (see ResolveEntityNames in
  // entity_translation_handler.go) — that resolution happens server-side at
  // fetch time, so this list needs to re-fetch whenever languageCode
  // changes, not just on mount — mirrors Categories.js's exact same pattern.
  const { languageCode } = useContext(I18nContext);
  const { t } = useTranslation("cities");
  const { t: tCommon } = useTranslation("common");
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1");
  const countryFilter = searchParams.get("country_id") || "";
  const zoneFilter = searchParams.get("zone_id") || "";

  const [countries, setCountries] = useState([]);
  const [zones, setZones] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, serverLimit: 10 });
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "asc" });
  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });
  const [status, setStatus] = useState("idle");

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchCities = async () => {
    try {
      const params = { page: currentPage };
      if (countryFilter) params.country_id = countryFilter;
      if (zoneFilter) params.zone_id = zoneFilter;
      const response = await getCities(token, params);
      setData({
        items: response.data || [],
        total: response.meta?.total || 0,
        serverLimit: response.meta?.limit || 10,
      });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getCountries(token).then(setCountries).catch(() => notify.error(t("notify_load_countries_failed", "Failed to load countries")));
  }, [languageCode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (countryFilter) {
      getZones(token, { country_id: countryFilter }).then(setZones).catch(() => setZones([]));
    } else {
      setZones([]);
    }
  }, [countryFilter, languageCode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchCities(); }, [currentPage, countryFilter, zoneFilter, languageCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateParams = (newParams) => {
    const current = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...current, ...newParams });
  };

  const countryOptions = useMemo(
    () => [{ value: "", label: t("filter_country_all_option", "All countries") }, ...countries.map((c) => ({ value: String(c.id), label: c.name }))],
    [countries, t]
  );
  const zoneOptions = useMemo(
    () => [{ value: "", label: t("filter_zone_all_option", "All zones") }, ...zones.map((z) => ({ value: String(z.id), label: z.name }))],
    [zones, t]
  );

  const sortedItems = useMemo(() => {
    const result = [...data.items];
    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [data.items, sortConfig]);

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  const totalPages = Math.ceil(data.total / data.serverLimit) || 1;

  const handleDeleteClick = (id) => setModalConfig({ isOpen: true, id });

  const confirmDelete = async () => {
    try {
      setStatus("loading");
      await deleteCity(token, modalConfig.id);
      setStatus("success");
      setTimeout(() => {
        setModalConfig({ isOpen: false, id: null });
        setStatus("idle");
      }, 1200);
      notify.success(t("notify_deleted", "City deleted successfully"));
      fetchCities();
    } catch (err) {
      setStatus("error");
      notify.error(err.message || t("notify_delete_failed", "Failed to delete city"));
    }
  };

  const columns = [
    { key: "id", label: tCommon("table_col_id", "ID"), className: "text-gray-400 font-mono" },
    { key: "name", label: t("col_name", "City"), className: "font-bold text-gray-700" },
    {
      key: "population",
      label: t("col_population", "Population"),
      className: "text-gray-600",
      render: (row) => (row.population ? row.population.toLocaleString() : "—"),
    },
    { key: "timezone", label: t("col_timezone", "Timezone"), className: "text-gray-600" },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={t("delete_confirm_title", "Delete City")}
        message={t("delete_confirm_message", "Are you sure? This will permanently remove this city.")}
        onConfirm={confirmDelete}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
        isLoading={status === "loading"}
        isSuccess={status === "success"}
        isError={status === "error"}
      />

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Cities size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Cities")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Manage cities within each zone/country")}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <ImportExportBar
            entityLabel="Cities"
            fileBaseName="cities"
            onExport={() => exportCities(token, { ...(countryFilter ? { country_id: countryFilter } : {}), ...(zoneFilter ? { zone_id: zoneFilter } : {}) })}
            onImport={(file) => importCities(token, file)}
            onImported={fetchCities}
          />
          <Button variant="primary" icon={ICONS.Plus} onClick={() => navigate("/add-city")} className="w-full sm:w-auto shrink-0">
            {t("btn_add_city", "Add City")}
          </Button>
        </div>
      </PageHeader>

      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
        <SelectBox
          label={t("filter_country_label", "Filter by country")}
          options={countryOptions}
          value={countryFilter}
          onChange={(val) => updateParams({ country_id: val, zone_id: "", page: 1 })}
        />
        <SelectBox
          label={t("filter_zone_label", "Filter by zone")}
          options={zoneOptions}
          value={zoneFilter}
          onChange={(val) => updateParams({ zone_id: val, page: 1 })}
        />
      </div>

      <DataTable
        columns={columns}
        data={sortedItems}
        sortConfig={sortConfig}
        onSort={handleSort}
        renderActions={(row, isMobile) => (
          <>
            <Button variant="outline" size="sm" icon={ICONS.EditUser} onClick={() => navigate(`/edit-city/${row.id}`)}>{isMobile && ` ${tCommon("btn_edit", "Edit")}`}</Button>
            <Button variant="danger" size="sm" icon={ICONS.DeleteUser} onClick={() => handleDeleteClick(row.id)}>{isMobile && ` ${tCommon("btn_delete", "Delete")}`}</Button>
          </>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">#{row.id}</span>
            <h3 className="font-bold text-gray-800">{row.name}</h3>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => updateParams({ page: p }),
          totalItems: data.total,
        }}
      />
    </div>
  );
}
